import React from "react";
import { Download, Save, Upload, X } from "lucide-react";
import { InlineAlert } from "../InlineAlert";
import { Spinner } from "../Spinner";
import { UnifiedTemplateManager } from "../UnifiedTemplateManager";
import { ContactsManager } from "../ContactsManager";
import { PasswordSettings } from "../PasswordSettings";

type SettingsTab = "general" | "types" | "contacts" | "access" | "import" | "danger" | "help";

type SettingsModalProps = {
  isOpen: boolean;
  structure: any;
  settingsTab: SettingsTab;
  setSettingsTab: (tab: SettingsTab) => void;
  settingsName: string;
  settingsDescription: string;
  setSettingsName: (v: string) => void;
  setSettingsDescription: (v: string) => void;
  settingsMessage: string | null;
  setSettingsMessage: (v: string | null) => void;
  settingsSaving: boolean;
  settingsDeleteLoading: boolean;
  importing: boolean;
  onClose: () => void;
  onSave: () => void;
  onDelete: () => void;
  onExport: () => void;
  onImportFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
  refetchNodeTypes: () => void;
  structureId?: string;
  navigateToDebug: () => void;
};

export function SettingsModal({
  isOpen,
  structure,
  settingsTab,
  setSettingsTab,
  settingsName,
  settingsDescription,
  setSettingsName,
  setSettingsDescription,
  settingsMessage,
  setSettingsMessage,
  settingsSaving,
  settingsDeleteLoading,
  importing,
  onClose,
  onSave,
  onDelete,
  onExport,
  onImportFile,
  refetchNodeTypes,
  structureId,
  navigateToDebug,
}: SettingsModalProps) {
  if (!isOpen || !structure) return null;

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center px-4">
      <div className="bg-[var(--color-panel)] text-[var(--color-text)] w-full max-w-2xl rounded-xl shadow-2xl overflow-hidden relative max-h-[90vh] flex flex-col">
        <button
          className="absolute right-3 top-3 text-[var(--color-muted)] hover:text-[var(--color-text)] z-10"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </button>
        <div className="p-6 border-b border-[var(--color-border)]">
          <h3 className="text-lg font-black title-font">Struktur-Einstellungen</h3>
        </div>

        <div className="flex flex-wrap gap-1 border-b border-[var(--color-border)] bg-[var(--color-panel-2)] px-2">
          {[
            { id: "general", label: "Allgemein" },
            { id: "types", label: "Typen" },
            { id: "contacts", label: "Kontakte" },
            { id: "access", label: "Zugriffskontrolle" },
            { id: "import", label: "Import/Export" },
            { id: "help", label: "Hilfe" },
            { id: "danger", label: "Löschen", danger: true },
          ].map((tab) => (
            <button
              key={tab.id}
              className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
                settingsTab === tab.id
                  ? `border-b-2 ${tab.danger ? "border-red-500 text-red-600 dark:text-red-400" : "border-blue-500 text-blue-600 dark:text-blue-400"}`
                  : "text-[var(--color-muted)] hover:text-[var(--color-text)]"
              }`}
              onClick={() => setSettingsTab(tab.id as SettingsTab)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {settingsTab === "general" && (
            <div className="space-y-4">
              {settingsMessage && (
                <InlineAlert tone="success" message={settingsMessage} dismissible onClose={() => setSettingsMessage(null)} />
              )}
              <div>
                <label className="text-xs text-[var(--color-muted)]">Name</label>
                <input
                  className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2"
                  value={settingsName}
                  onChange={(e) => setSettingsName(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-[var(--color-muted)]">Beschreibung</label>
                <textarea
                  className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2"
                  rows={4}
                  value={settingsDescription}
                  onChange={(e) => setSettingsDescription(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button className="btn btn-secondary" onClick={onClose}>
                  Abbrechen
                </button>
                <button className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed" onClick={onSave} disabled={settingsSaving || settingsName.trim().length === 0}>
                  {settingsSaving ? <Spinner size="sm" /> : <Save className="h-4 w-4" />} Speichern
                </button>
              </div>
            </div>
          )}

          {settingsTab === "types" && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-[var(--color-text)] mb-1">Node-Typen verwalten</h4>
                <p className="text-xs text-[var(--color-muted)] mb-4">
                  Wähle aus, welche Node-Typen verfügbar sind, und bearbeite ihre Farben und Formen.
                </p>
              </div>
              <UnifiedTemplateManager structureId={structureId} onVisibilityChanged={refetchNodeTypes} />
            </div>
          )}

          {settingsTab === "contacts" && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-[var(--color-text)] mb-1">Adressbuch</h4>
                <p className="text-xs text-[var(--color-muted)] mb-4">
                  Verwalte Kontakte für diese Struktur. Kontakte können in Nodes referenziert werden, um Duplikate zu vermeiden.
                </p>
              </div>
              <ContactsManager structureId={structureId} />
            </div>
          )}

          {settingsTab === "access" && (
            <PasswordSettings
              structure={structure}
              onUpdate={(updated) => {
                // No local navigation here, parent handles update
                setSettingsMessage("Gespeichert");
              }}
            />
          )}

          {settingsTab === "import" && (
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-semibold text-[var(--color-text)] mb-2">Backup</h4>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button className="btn btn-secondary" onClick={onExport} disabled={importing}>
                    <Download className="h-4 w-4" /> Export
                  </button>
                  <label className="btn btn-secondary cursor-pointer">
                    <Upload className="h-4 w-4" /> Import
                    <input type="file" accept="application/json" className="hidden" onChange={onImportFile} />
                  </label>
                </div>
                {importing && (
                  <div className="mt-2">
                    <Spinner label="Import läuft..." size="sm" className="text-[var(--color-muted)]" />
                  </div>
                )}
              </div>

              <div className="border-t border-[var(--color-border)] pt-4">
                <h4 className="text-sm font-semibold text-[var(--color-text)] mb-2">Debug</h4>
                <button className="btn btn-secondary" onClick={navigateToDebug}>
                  Debug Graph
                </button>
              </div>

              <div className="border-t border-[var(--color-border)] pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-[var(--color-text)]">Screenshot (PNG)</h4>
                  <div className="text-xs text-[var(--color-muted)]">PNG Export ist im Editor-Header konfigurierbar.</div>
                </div>
              </div>
            </div>
          )}

          {settingsTab === "help" && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-[var(--color-text)]">Tastaturkürzel</h4>
              <div className="text-sm text-[var(--color-muted)]">Alle Kürzel findest du im ? Overlay.</div>
            </div>
          )}

          {settingsTab === "danger" && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <div className="text-sm font-semibold text-red-700 dark:text-red-300 mb-1">Gefahrzone</div>
                <div className="text-sm text-red-700 dark:text-red-300">
                  Das Löschen entfernt die gesamte Struktur inklusive aller Nodes und Kanten.
                </div>
              </div>
              <button
                className="btn bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={settingsDeleteLoading}
                onClick={onDelete}
              >
                {settingsDeleteLoading ? <Spinner size="sm" /> : "Struktur löschen"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
